import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Database, Tags, Network, Clapperboard, BarChart3, GaugeCircle, FolderSync } from 'lucide-react';

const features = [
  { icon: Database, title: 'Rekordbox DB Sync', desc: 'Import and export against Rekordbox 6/7 databases while preserving playlists, cues, and metadata.' },
  { icon: Tags, title: 'Quick Tag Mode', desc: 'Run fast file-scoped tagging sessions for folders without changing your Rekordbox workflow.' },
  { icon: Network, title: 'PathFlo', desc: 'Visualize energy and key transitions, then save a playable track order to your library.' },
  { icon: Clapperboard, title: 'JourneyFlo', desc: 'Plan structured DJ sets with chapter-style blocks and export-ready timing.' },
  { icon: BarChart3, title: 'Analytics Screen', desc: 'Understand genre, key, BPM, and library mix with charts built for set prep.' },
  { icon: GaugeCircle, title: 'Benchmark Center', desc: 'Run built-in benchmarks to measure scan, sort, and cache performance at scale.' },
  { icon: FolderSync, title: 'Watch Folder', desc: 'Auto-ingest new tracks from a folder and apply enrichment pipelines without manual rescans.' },
];

export default function Features() {
  const ref = useRef(null);
  const v = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="features" data-testid="features-section" ref={ref} className="section section--border-top">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={v ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <span className="eyebrow">Features</span>
          <h2 data-testid="features-title" className="section-title">Built for real DJ workflows</h2>
        </motion.div>

        <div className="features-grid feat-grid-v3">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                data-testid={`feature-card-${i}`}
                className="feature-card"
                initial={{ opacity: 0, y: 20 }}
                animate={v ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1 + i * 0.06 }}
              >
                <Icon className="feature-icon" size={20} strokeWidth={1.5} aria-hidden="true" />
                <h3 className="feature-title">{f.title}</h3>
                <p className="feature-desc">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
